import { Fragment, useRef, useState } from "react";
import { operationId } from "./operationId";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import ChatBubble from "@mui/icons-material/ChatBubble";
import ReportProblem from "@mui/icons-material/ReportProblem";
import Email from "@mui/icons-material/Email";
import DraftsOutlined from "@mui/icons-material/DraftsOutlined";
import Warehouse from "@mui/icons-material/Warehouse";
import NewReleases from "@mui/icons-material/NewReleases";
import Close from "@mui/icons-material/Close";
import Event from "@mui/icons-material/Event";
import DeleteForever from "@mui/icons-material/DeleteForever";
import RemoveCircle from "@mui/icons-material/RemoveCircle";
import { plantStatusLabels, plantTime } from "./plantOrderFormatting";

export function PlantOrderTime({
  original,
  changed,
}: {
  original?: string | null;
  changed?: string | null;
}) {
  return (
    <Fragment>
      <span className="transtime">{changed}</span>
      <span
        className={
          "plantime " +
          (!changed
            ? "notrans"
            : changed === original || !original
              ? "sametrans"
              : "difftrans")
        }
      >
        {plantTime(original)}
      </span>
    </Fragment>
  );
}

const statusPresentation: Record<string, [typeof Email, string, string]> = {
  OPEN: [Email, "READ", "darkblue"],
  READ: [DraftsOutlined, "OPEN", "green"],
  PACKED: [Warehouse, "READ", "grey"],
  CHANGED: [NewReleases, "READ", "orange"],
  RETRACTED: [Close, "RETRACTED", "#777"],
  MOVED: [Event, "READ", "orange"],
  STORNO: [DeleteForever, "DELETED", "red"],
  DELETED: [RemoveCircle, "STORNO", "#777"],
};

export function PlantStatusButton({
  status,
  nextStatus,
  disabled = false,
  icon,
  color,
  label = plantStatusLabels[status] ?? status,
  save,
}: {
  status: string;
  nextStatus?: string;
  disabled?: boolean;
  icon?: typeof Email;
  color?: string;
  label?: string;
  save: (next: string) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const presentation = statusPresentation[status];
  const next = nextStatus ?? presentation?.[1];
  const Icon = icon ?? presentation?.[0] ?? Email;
  return busy ? (
    <CircularProgress />
  ) : (
    <Tooltip title={label}>
      <Icon
        role="button"
        aria-hidden={false}
        aria-label={`Werkstatus: ${label}`}
        aria-disabled={disabled || !next}
        sx={{
          cursor: "pointer",
          color: color ?? presentation?.[2],
          "&:hover": { color: "#666" },
        }}
        onClick={
          disabled || !next
            ? undefined
            : async () => {
                setBusy(true);
                try {
                  await save(next);
                } catch {
                  // Each application's API adapter handles refresh and error feedback.
                } finally {
                  setBusy(false);
                }
              }
        }
      />
    </Tooltip>
  );
}

export function PlantCommentButton({
  comment,
  critical,
  readOnly = false,
  save,
}: {
  comment: string;
  critical: boolean;
  readOnly?: boolean;
  save: (
    data: { plantComment: string; critical: boolean },
    requestId: string,
  ) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(comment);
  const [flag, setFlag] = useState(critical);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const request = useRef<string | null>(null);
  const valid = !flag || draft.trim().length > 0;
  const Icon = critical
    ? ReportProblem
    : comment.length > 0
      ? ChatBubble
      : ChatBubbleOutline;
  return (
    <div className="mtcommentwrap">
      <Icon
        role="button"
        aria-hidden={false}
        aria-label="Werkskommentar"
        fontSize="inherit"
        sx={{
          cursor: "pointer",
          marginLeft: "4px",
          verticalAlign: "middle",
          color: critical ? "darkred" : "#444",
          "&:hover": { color: "#666" },
        }}
        onClick={() => {
          setDraft(comment);
          setFlag(critical);
          request.current = null;
          setOpen(true);
        }}
      />
      {open ? (
        <Dialog
          open={open}
          maxWidth="md"
          fullWidth
          onClose={() => setOpen(false)}
        >
          <DialogTitle
            sx={{
              p: 1,
              px: 2,
              background: "#002169",
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.5)",
            }}
          >
            Interner Werkskommentar
          </DialogTitle>
          <DialogContent sx={{ p: 1 }}>
            <TextField
              size="small"
              sx={{ mt: 1 }}
              fullWidth
              value={draft}
              placeholder="Kommentar"
              inputProps={{ "aria-label": "Kommentar" }}
              multiline
              disabled={readOnly}
              error={!valid}
              helperText={
                !valid ? "Für Kritisch ist ein Kommentar erforderlich." : ""
              }
              onChange={(e) => {
                setDraft(e.target.value);
                request.current = null;
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={flag}
                  disabled={readOnly}
                  onChange={(e) => {
                    setFlag(e.target.checked);
                    request.current = null;
                  }}
                />
              }
              label="Kritisch"
            />
            {error ? (
              <Alert severity="error">
                Speichern fehlgeschlagen. Bitte erneut versuchen.
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "1px solid #aaa",
              justifyContent: "space-between",
            }}
          >
            <Button onClick={() => setOpen(false)}>Schließen</Button>
            {!readOnly ? (
              <Button
                variant="contained"
                disabled={!valid || busy}
                onClick={async () => {
                  setBusy(true);
                  setError(false);
                  request.current ??= operationId();
                  try {
                    await save(
                      { plantComment: draft, critical: flag },
                      request.current,
                    );
                    setOpen(false);
                  } catch {
                    setError(true);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Speichern
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>
      ) : null}
    </div>
  );
}
